import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { "data-cy": "add-update-glossary-modal" }
const _hoisted_2 = { class: "font-weight-bold" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "font-weight-bold" }
const _hoisted_5 = { class: "button-content" }
const _hoisted_6 = { class: "font-weight-bold" }
const _hoisted_7 = { class: "button-content" }

import { reactive, computed, watch, ref } from "vue";
import { GlossaryTypes } from "@medapro-types/AttributeManager/GlossaryTypes";
import { useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";

type ViewModel = {
  isUpdateGlossary: boolean;
  isAddGlossary: boolean;
  selectedGlossary: GlossaryTypes.GlossaryItem | null;
  selectedSourceLanguage: GlossaryTypes.glossaryLanguage | null;
  selectedTargetLanguage: GlossaryTypes.glossaryLanguage | null;
  availableSourceLanguages: GlossaryTypes.glossaryLanguage[];
  availableTargetLanguages: GlossaryTypes.glossaryLanguage[];
  inputGlossaryName: string;
  glossaryItems: GlossaryTypes.GlossaryItem[];
  glossaryLanguagePairs: GlossaryTypes.getGlossaryLanguagePairs[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AddUpdateGlossaryModal',
  props: {
    showModal: { type: Boolean },
    glossaryItems: {},
    selectedGlossary: {},
    selectedSourceLanguage: {},
    selectedTargetLanguage: {},
    inputGlossaryName: {},
    availableSourceLanguages: {},
    availableTargetLanguages: {},
    glossaryLanguagePairs: {},
    isUpdateGlossary: { type: Boolean },
    isAddGlossary: { type: Boolean }
  },
  emits: ["update:showModal", "addOrUpdateFromModal"],
  setup(__props: any, { emit }) {

const props = __props;

const input = ref<HTMLInputElement | null>(null);




const { t: $t } = useI18n();

//#region Viewmodel

const vm = reactive<ViewModel>({
  isUpdateGlossary: false,
  isAddGlossary: false,
  selectedGlossary: null,
  availableSourceLanguages: [],
  availableTargetLanguages: [],
  selectedSourceLanguage: null,
  selectedTargetLanguage: null,
  inputGlossaryName: "",
  glossaryItems: [],
  glossaryLanguagePairs: []
});

//#endregion

//#region Validation

const validationFeedback = computed(() => {
  const glossaryItemInput = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  const addUpdateInputsEmpty = vm.inputGlossaryName.length !== 0 && vm.selectedSourceLanguage !== null && vm.selectedTargetLanguage !== null;
  const languagePairValid = vm.selectedSourceLanguage !== vm.selectedTargetLanguage;
  return addUpdateInputsEmpty && languagePairValid && !isDublicatedGlossar(glossaryItemInput) && checkGlossaryLanguagePairIsValid() ? true : false;
});

const nameValidationFeedback = computed(() => {
  const glossaryItemInput: GlossaryTypes.GlossaryItem = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  if (vm.isAddGlossary) {
    glossaryItemInput.id = "";
  } else {
    if (!vm.selectedGlossary) return;
    glossaryItemInput.id = vm.selectedGlossary.id;
  }

  const isNameEmpty = vm.inputGlossaryName == null || vm.inputGlossaryName == "";
  if (isNameEmpty) return $t("Glossary.NameIsEmpty");
  else if (isDublicatedGlossar(glossaryItemInput)) return $t("Glossary.GlossaryExists");
  else if (vm.inputGlossaryName.trim().length > 255) return $t("Glossary.AnEntryHasToMuchChars");
  else return undefined;
});

const languagePairValidationFeedback = computed(() => {
  const glossaryItemInput: GlossaryTypes.GlossaryItem = {
    id: "",
    name: vm.inputGlossaryName,
    sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
    targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
    isActive: false
  };
  if (vm.isAddGlossary) {
    glossaryItemInput.id = "";
  } else {
    if (!vm.selectedGlossary) return;
    glossaryItemInput.id = vm.selectedGlossary.id;
  }
  if (isDublicatedGlossar(glossaryItemInput)) return $t("Glossary.ChooseLanguagePairWhichNotExist");
  else if (vm.selectedSourceLanguage == null && vm.selectedTargetLanguage == null) return undefined;
  else if (vm.selectedSourceLanguage?.languageCode == vm.selectedTargetLanguage?.languageCode) return $t("Glossary.ChooseLanguagePairWithDifferentValues");
  else return undefined;
});

//#endregion

//#region Manipulation / Helperfunctions

const computedGlossaryAddUpdateTitle = computed(() => {
  if (vm.isUpdateGlossary) {
    return $t("Glossary.GlossaryEditTitle");
  }
  return $t("Glossary.GlossaryAddTitle");
});

const _showModal = computed({
  get: () => {
    return props.showModal;
  },
  set: (nv) => {
    emit("update:showModal", nv);
  }
});

function handleSourceLanguageSelect(value: GlossaryTypes.glossaryLanguage) {
  vm.selectedSourceLanguage = value;
}
function handleTargetLanguageSelect(value: GlossaryTypes.glossaryLanguage) {
  vm.selectedTargetLanguage = value;
}

function isDublicatedGlossar(targetObject: GlossaryTypes.GlossaryItem) {
  if (vm.isUpdateGlossary) {
    for (let i = 0; i < vm.glossaryItems.length; i++) {
      const obj = vm.glossaryItems[i];
      if (
        obj.id !== targetObject.id &&
        obj.name === targetObject.name &&
        obj.sourceLanguage === targetObject.sourceLanguage &&
        obj.targetLanguage === targetObject.targetLanguage
      ) {
        return true;
      }
    }
    return false;
  } else {
    for (let i = 0; i < vm.glossaryItems.length; i++) {
      const obj = vm.glossaryItems[i];
      if (obj.name === targetObject.name && obj.sourceLanguage === targetObject.sourceLanguage && obj.targetLanguage === targetObject.targetLanguage) {
        return true;
      }
    }
    return false;
  }
}

function checkGlossaryLanguagePairIsValid() {
  const glossaryLanguagePairToCheck = {
    sourceLanguageCode: vm.selectedSourceLanguage?.languageCode,
    targetLanguageCode: vm.selectedTargetLanguage?.languageCode
  };
  const isGlossaryLanguagePairValid = vm.glossaryLanguagePairs.find(
    (x) =>
      x.sourceLanguage.languageCode == glossaryLanguagePairToCheck.sourceLanguageCode &&
      x.targetLanguage.languageCode == glossaryLanguagePairToCheck.targetLanguageCode
  );
  return isGlossaryLanguagePairValid !== undefined;
}

function AddOrUpdate() {
  if (vm.isUpdateGlossary) {
    if (!vm.selectedGlossary) return;
    const createdOrUpdatedGlossary: GlossaryTypes.GlossaryItemToUpdateExtended = {
      updateModel: {
        id: vm.selectedGlossary.id,
        name: vm.selectedGlossary.name,
        sourceLanguage: vm.selectedGlossary.sourceLanguage,
        targetLanguage: vm.selectedGlossary.targetLanguage,
        isActive: false,
        newName: vm.inputGlossaryName,
        newSourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
        newTargetLanguage: vm.selectedTargetLanguage?.languageCode as string
      }
    };
    emit("addOrUpdateFromModal", createdOrUpdatedGlossary, false, false);
  } else {
    const createdOrUpdatedGlossary: GlossaryTypes.GlossaryItemToUpdate = {
      updateModel: {
        id: "",
        name: vm.inputGlossaryName,
        sourceLanguage: vm.selectedSourceLanguage?.languageCode as string,
        targetLanguage: vm.selectedTargetLanguage?.languageCode as string,
        isActive: false
      },
      entries: {}
    };
    emit("addOrUpdateFromModal", createdOrUpdatedGlossary, false, false);
  }
}

//#endregion

//#region Watcher

watch(
  () => props,
  () => {
    if (input.value) {
      useFocus(input, { initialValue: true });
    }
    Object.assign(vm, props);
  },
  { deep: true }
);

//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_form_group = _resolveComponent("b-form-group")!
  const _component_b_dropdown_item = _resolveComponent("b-dropdown-item")!
  const _component_b_dropdown = _resolveComponent("b-dropdown")!
  const _component_b_col = _resolveComponent("b-col")!
  const _component_b_row = _resolveComponent("b-row")!
  const _component_BFormGroup = _resolveComponent("BFormGroup")!
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_modal = _resolveComponent("b-modal")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    "header-close-content": "",
    modelValue: _showModal.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_showModal).value = $event)),
    title: computedGlossaryAddUpdateTitle.value
  }, {
    "modal-footer": _withCtx(({ cancel }) => [
      _createVNode(_component_b_btn, {
        onClick: AddOrUpdate,
        disabled: !validationFeedback.value || nameValidationFeedback.value !== undefined,
        variant: "primary",
        "data-cy": "add-update-glossary-save-btn"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("Save")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_b_btn, { onClick: cancel }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("Cancel")), 1)
        ]),
        _: 2
      }, 1032, ["onClick"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_b_form_group, null, {
          default: _withCtx(() => [
            _createElementVNode("label", _hoisted_2, _toDisplayString(_unref($t)("Glossary.Name")), 1),
            _withDirectives(_createElementVNode("input", {
              type: "text",
              ref_key: "input",
              ref: input,
              class: _normalizeClass(["form-control", { 'border-danger': nameValidationFeedback.value }]),
              placeholder: _unref($t)('Glossary.EnterName'),
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((vm.inputGlossaryName) = $event)),
              "data-cy": "glossary-name-input"
            }, null, 10, _hoisted_3), [
              [_vModelText, vm.inputGlossaryName],
              [_directive_tooltip, nameValidationFeedback.value]
            ])
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", null, [
        _createVNode(_component_BFormGroup, null, {
          default: _withCtx(() => [
            _createVNode(_component_b_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_b_col, { class: "col-6" }, {
                  default: _withCtx(() => [
                    _createElementVNode("label", _hoisted_4, _toDisplayString(_unref($t)("Glossary.SourceLanguage")), 1),
                    _withDirectives((_openBlock(), _createBlock(_component_b_dropdown, {
                      class: _normalizeClass(["w-100 dropdown-default", { 'border-danger': languagePairValidationFeedback.value }]),
                      "data-cy": "source-language-dropdown"
                    }, {
                      "button-content": _withCtx(() => [
                        _createElementVNode("span", _hoisted_5, _toDisplayString(vm.selectedSourceLanguage == null ? `${_unref($t)("Glossary.ChooseSourceLanguage")}` : vm.selectedSourceLanguage.displayName), 1)
                      ]),
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vm.availableSourceLanguages, (lang, key) => {
                          return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                            key: key,
                            "data-cy": 'source-language-' + lang.languageCode,
                            onClick: ($event: any) => (handleSourceLanguageSelect(lang))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(lang.displayName), 1)
                            ]),
                            _: 2
                          }, 1032, ["data-cy", "onClick"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["class"])), [
                      [_directive_tooltip, languagePairValidationFeedback.value]
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_component_b_col, { class: "col-6" }, {
                  default: _withCtx(() => [
                    _createElementVNode("label", _hoisted_6, _toDisplayString(_unref($t)("Glossary.TargetLanguage")), 1),
                    _withDirectives((_openBlock(), _createBlock(_component_b_dropdown, {
                      class: _normalizeClass(["w-100 dropdown-default", { 'border-danger': languagePairValidationFeedback.value }]),
                      "data-cy": "target-language-dropdown"
                    }, {
                      "button-content": _withCtx(() => [
                        _createElementVNode("span", _hoisted_7, _toDisplayString(vm.selectedTargetLanguage == null ? `${_unref($t)("Glossary.ChooseTargetLanguage")}` : vm.selectedTargetLanguage.displayName), 1)
                      ]),
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vm.availableTargetLanguages, (lang, key) => {
                          return (_openBlock(), _createBlock(_component_b_dropdown_item, {
                            key: key,
                            "data-cy": 'target-language-' + lang.languageCode,
                            onClick: ($event: any) => (handleTargetLanguageSelect(lang))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(lang.displayName), 1)
                            ]),
                            _: 2
                          }, 1032, ["data-cy", "onClick"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["class"])), [
                      [_directive_tooltip, languagePairValidationFeedback.value]
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})